import { useState, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import SectionLabel from '../common/SectionLabel';
import { usePipeline } from '../../context/PipelineContext';
import { useFinancialData, SCENARIO_CONFIG } from '../../context/FinancialDataContext';
import { ACTUALS_CUTOFF_2026 } from '../../context/FinancialDataContext';
import { formatCurrency } from '../../utils/formatting';
import { SERVICE_TIERS, PIPELINE_STAGES, calculateAnnualizedValue, getEffectiveProbability, computeAvgDaysToClose, DEFAULT_DAYS_TO_CLOSE as DEFAULT_DAYS_FALLBACK } from '../../utils/pipelineCalculations';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

const DIVISIONS = ['bwm', 'bts', 'bia'];
const DIVISION_LABELS = { bwm: 'BWM', bts: 'BTS', bia: 'BIA' };
const DIVISION_FULL_LABELS = {
  bwm: 'Bullogic Wealth Management',
  bts: 'Bullogic Tax Services',
  bia: 'Bullogic Insurance Agency',
};

// Brand colors for each division (used in chart)
const DIVISION_COLORS = {
  bwm: '#2596BE', // Bullogic Blue
  bts: '#006D78', // Deep Teal
  bia: '#3EAAD0', // Sky Blue
};

// Default conversion rates (prospect-to-close) per division
const DEFAULT_CONVERSION = { bwm: 0.25, bts: 0.40, bia: 0.60 };

// Days to close: starts as fallback; replaced with observed values when prospects load
let DEFAULT_DAYS_TO_CLOSE = { ...DEFAULT_DAYS_FALLBACK };

// Default prospect inflow if we have zero data
const DEFAULT_MONTHLY_PROSPECTS = { bwm: 2, bts: 3, bia: 1 };

// Seasonal multipliers by division and month index (0 = Jan)
const SEASONAL_MULTIPLIERS = {
  bts: [1.5, 1.5, 1.5, 1.5, 0.6, 0.6, 0.6, 0.6, 1.3, 1.3, 0.7, 0.7],
  bwm: [1.1, 1.1, 1.1, 1.0, 1.0, 1.0, 0.9, 0.9, 0.9, 1.0, 1.0, 1.0],
  bia: [1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0],
};

// Seasonal labels for the detail table
function getSeasonLabel(division, monthIdx) {
  const m = SEASONAL_MULTIPLIERS[division]?.[monthIdx] ?? 1.0;
  if (m >= 1.3) return 'Peak';
  if (m <= 0.7) return 'Slow';
  return 'Normal';
}

// Average annual deal value per division, computed from SERVICE_TIERS defaults
// BWM blended: average of Guided ($7,800) and Total ($21,900) = $14,850
// BTS blended: average of Individual ($1,500) and Business ($3,000) = $2,250
// BIA: $500 default (commission-based, no standard tier)
const DEFAULT_DEAL_VALUES = { bwm: 14850, bts: 2250, bia: 500 };

// ---------------------------------------------------------------------------
// Helper: derive division from a prospect
// ---------------------------------------------------------------------------
function getProspectDivision(prospect) {
  if (prospect.division) return prospect.division;
  const tier = SERVICE_TIERS[prospect.serviceTier];
  return tier?.division || 'other';
}

// ---------------------------------------------------------------------------
// Layer 1: Historical velocity -- prospects added per month, by division
// ---------------------------------------------------------------------------
function computeHistoricalVelocity(prospects) {
  // Group prospect createdDate by month and division
  const monthCounts = {}; // { 'bwm': { 0: 3, 1: 2, ... }, ... }
  const monthsWithData = new Set();

  for (const div of DIVISIONS) {
    monthCounts[div] = {};
  }

  for (const p of prospects) {
    if (!p.createdDate) continue;
    const div = getProspectDivision(p);
    if (!DIVISIONS.includes(div)) continue;
    const monthIdx = new Date(p.createdDate).getMonth();
    monthCounts[div][monthIdx] = (monthCounts[div][monthIdx] || 0) + 1;
    monthsWithData.add(monthIdx);
  }

  // Compute average monthly rate per division
  const rates = {};
  const numMonths = monthsWithData.size || 1;

  for (const div of DIVISIONS) {
    const total = Object.values(monthCounts[div]).reduce((s, v) => s + v, 0);
    // If we have data, use observed rate; otherwise fall back to default
    rates[div] = total > 0 ? total / numMonths : DEFAULT_MONTHLY_PROSPECTS[div];
  }

  return { rates, monthCounts, numMonths, hasData: monthsWithData.size > 0 };
}

// ---------------------------------------------------------------------------
// Compute actual average deal value from prospect data per division
// ---------------------------------------------------------------------------
function computeAvgDealValues(prospects) {
  const sums = {};
  const counts = {};

  for (const div of DIVISIONS) {
    sums[div] = 0;
    counts[div] = 0;
  }

  for (const p of prospects) {
    const div = getProspectDivision(p);
    if (!DIVISIONS.includes(div)) continue;
    const val = calculateAnnualizedValue(p.estimatedValue);
    if (val > 0) {
      sums[div] += val;
      counts[div] += 1;
    }
  }

  const result = {};
  for (const div of DIVISIONS) {
    result[div] = counts[div] >= 2 ? Math.round(sums[div] / counts[div]) : DEFAULT_DEAL_VALUES[div];
  }
  return result;
}

// ---------------------------------------------------------------------------
// Compute observed conversion rates from closed prospects
// ---------------------------------------------------------------------------
function computeObservedConversion(prospects) {
  const won = {};
  const total = {};
  for (const div of DIVISIONS) { won[div] = 0; total[div] = 0; }

  for (const p of prospects) {
    const div = getProspectDivision(p);
    if (!DIVISIONS.includes(div)) continue;
    if (p.outcome === 'won') { won[div]++; total[div]++; }
    else if (p.outcome === 'lost' || p.outcome === 'unqualified') { total[div]++; }
  }

  const rates = {};
  for (const div of DIVISIONS) {
    // Need at least 3 outcomes to trust observed data
    rates[div] = total[div] >= 3 ? won[div] / total[div] : DEFAULT_CONVERSION[div];
  }
  return rates;
}

// ---------------------------------------------------------------------------
// Revenue recognition logic for a single deal
// Returns array of 12 monthly amounts (index 0=Jan, 11=Dec)
// ---------------------------------------------------------------------------
function recognizeRevenue(division, avgDealValue, closeMonthIdx) {
  const monthly = new Array(12).fill(0);
  if (closeMonthIdx < 0 || closeMonthIdx > 11) return monthly;

  if (division === 'bts') {
    // Tax prep: revenue recognized in April (month 3) or close month if after April
    const recogMonth = Math.max(closeMonthIdx, 3);
    if (recogMonth < 12) monthly[recogMonth] += avgDealValue;
  } else if (division === 'bwm') {
    // BWM blended model: assume mix of one-time and recurring
    // Approximate: 40% as one-time (50% at close, 50% three months later)
    // and 60% as recurring monthly from close through Dec
    const oneTimePortion = avgDealValue * 0.4;
    const recurringAnnual = avgDealValue * 0.6;
    const monthlyRecurring = recurringAnnual / 12;

    // One-time: 50% at close, 50% three months later
    if (closeMonthIdx < 12) monthly[closeMonthIdx] += oneTimePortion * 0.5;
    const laterMonth = closeMonthIdx + 3;
    if (laterMonth < 12) monthly[laterMonth] += oneTimePortion * 0.5;

    // Recurring: monthly from close through December
    for (let m = closeMonthIdx; m < 12; m++) {
      monthly[m] += monthlyRecurring;
    }
  } else if (division === 'bia') {
    // Insurance commission: recognized at close month
    if (closeMonthIdx < 12) monthly[closeMonthIdx] += avgDealValue;
  }

  return monthly;
}

// ---------------------------------------------------------------------------
// Layer 2: Pipeline revenue from existing active prospects.
// Uses each prospect's effective probability (smart decay + manual override)
// and recognizes revenue at their expected close month.
// ---------------------------------------------------------------------------
function computePipelineRevenue(prospects, sourceStats) {
  // Recompute days-to-close from current data (won deals)
  DEFAULT_DAYS_TO_CLOSE = computeAvgDaysToClose(prospects || []);
  // revenueGrid[monthIdx] = { bwm, bts, bia }
  const grid = new Array(12).fill(null).map(() => ({ bwm: 0, bts: 0, bia: 0 }));
  // Per-month deal counts (for pipeline-aware velocity to subtract)
  const dealsGrid = new Array(12).fill(null).map(() => ({ bwm: 0, bts: 0, bia: 0 }));

  const active = prospects.filter(p =>
    p.stage !== 'closed-won' && p.stage !== 'closed-lost' && p.stage !== 'unqualified'
  );

  for (const p of active) {
    const div = getProspectDivision(p);
    if (!['bwm', 'bts', 'bia'].includes(div)) continue;

    const annualValue = calculateAnnualizedValue(p.estimatedValue);
    const { probability } = getEffectiveProbability(p, sourceStats);
    const weightedValue = annualValue * probability;
    if (weightedValue <= 0) continue;

    // Determine the month revenue is expected to start. Prefer
    // serviceStartDate (most accurate for tax-prep deals signed mid-year
    // for next year's filing); fall back to expectedCloseDate; fall back
    // to today + days-to-close. Year-gate: if revenue starts outside
    // 2026, skip this prospect entirely — it doesn't contribute to the
    // 2026 forecast.
    let closeMonth;
    const sourceDate = p.serviceStartDate || p.expectedCloseDate;
    if (sourceDate) {
      const d = new Date(sourceDate);
      if (d.getFullYear() !== 2026) continue;
      closeMonth = d.getMonth();
    } else {
      const today = new Date();
      const daysOut = DEFAULT_DAYS_TO_CLOSE[div] || 30;
      const projected = new Date(today.getTime() + daysOut * 24 * 60 * 60 * 1000);
      if (projected.getFullYear() !== 2026) continue;
      closeMonth = projected.getMonth();
    }
    closeMonth = Math.max(ACTUALS_CUTOFF_2026, Math.min(closeMonth, 11));

    // Recognize the weighted revenue at close month
    const dealRevenue = recognizeRevenue(div, weightedValue, closeMonth);
    for (let rm = 0; rm < 12; rm++) {
      grid[rm][div] += dealRevenue[rm];
    }
    // Track expected deals for this month (probability-weighted count)
    dealsGrid[closeMonth][div] += probability;
  }

  return { grid, dealsGrid };
}

// ---------------------------------------------------------------------------
// Filter prospects by min stage order (for conservative scenario)
// ---------------------------------------------------------------------------
function filterByStageOrder(prospects, minOrder) {
  if (!minOrder || minOrder <= 1) return prospects;
  return prospects.filter(p => {
    const stage = PIPELINE_STAGES.find(s => s.id === p.stage);
    if (!stage) return false;
    if (stage.order >= 6) return true; // Always include closed-won
    return stage.order >= minOrder;
  });
}

// ---------------------------------------------------------------------------
// Main forecast engine (3-layer + scenario aware)
// ---------------------------------------------------------------------------
function runForecast(prospects, adjustments, committedRevenue, convOverrides, scenario = 'normal', sourceStats = null) {
  const config = SCENARIO_CONFIG[scenario] || SCENARIO_CONFIG.normal;
  const today = new Date();
  const todayMonth = today.getMonth();
  // Recompute days-to-close from current prospects (won deals only)
  DEFAULT_DAYS_TO_CLOSE = computeAvgDaysToClose(prospects || []);

  // ---- LAYER 2: Pipeline (existing prospects) ----
  // Filter prospects by stage threshold for conservative scenario
  const pipelineProspects = filterByStageOrder(prospects, config.pipelineMinStageOrder);
  const pipelineLayer = computePipelineRevenue(pipelineProspects, sourceStats);
  // Apply scenario multiplier
  for (let m = 0; m < 12; m++) {
    for (const div of DIVISIONS) {
      pipelineLayer.grid[m][div] *= config.pipelineMultiplier;
      pipelineLayer.dealsGrid[m][div] *= config.pipelineMultiplier;
    }
  }

  // ---- LAYER 3: Velocity (new prospects we haven't met) ----
  const velocity = computeHistoricalVelocity(prospects);
  const avgDealValues = computeAvgDealValues(prospects);
  const baseConversionRates = computeObservedConversion(prospects);
  const conversionRates = { ...baseConversionRates };
  // Apply scenario conversion adjustment (-10%, 0, +10%)
  for (const div of DIVISIONS) {
    conversionRates[div] = Math.max(0.05, Math.min(0.95, conversionRates[div] + config.conversionAdjust));
  }
  // What-if conversion overrides take precedence
  if (convOverrides) {
    for (const div of DIVISIONS) {
      if (convOverrides[div] != null) conversionRates[div] = convOverrides[div];
    }
  }

  // Velocity revenue grid (separate from pipeline, will be combined later)
  const velocityGrid = new Array(12).fill(null).map(() => ({ bwm: 0, bts: 0, bia: 0 }));
  const velocityDealsGrid = new Array(12).fill(null).map(() => ({ bwm: 0, bts: 0, bia: 0 }));

  for (let m = ACTUALS_CUTOFF_2026; m < 12; m++) {
    for (const div of DIVISIONS) {
      const baseRate = velocity.rates[div];
      const seasonal = SEASONAL_MULTIPLIERS[div][m];
      const userAdj = adjustments[div] ?? 1.0;
      // Apply scenario velocity multiplier
      const prospectCount = baseRate * seasonal * userAdj * config.velocityMultiplier;
      const expectedDeals = prospectCount * conversionRates[div];

      // Time-shift: velocity revenue is from NEW prospects who need full sales cycle
      const daysToClose = DEFAULT_DAYS_TO_CLOSE[div];
      const closeMonthOffset = Math.round(daysToClose / 30);
      const closeMonth = Math.min(m + closeMonthOffset, 11);

      const dealRevenue = recognizeRevenue(div, avgDealValues[div], closeMonth);
      for (let rm = 0; rm < 12; rm++) {
        velocityGrid[rm][div] += dealRevenue[rm] * expectedDeals;
      }
      velocityDealsGrid[closeMonth][div] += expectedDeals;
    }
  }

  // ---- PIPELINE-AWARE: Subtract pipeline coverage from velocity ----
  // For each month/division, if pipeline already covers the deals, reduce velocity.
  // Velocity for a month = max(0, velocity_deals - pipeline_deals) * proportional revenue
  const adjustedVelocityGrid = new Array(12).fill(null).map(() => ({ bwm: 0, bts: 0, bia: 0 }));
  for (let m = 0; m < 12; m++) {
    for (const div of DIVISIONS) {
      const velDeals = velocityDealsGrid[m][div];
      const pipeDeals = pipelineLayer.dealsGrid[m][div];
      if (velDeals <= 0) continue;
      // Reduce velocity revenue proportionally to pipeline coverage
      const overlap = Math.min(pipeDeals, velDeals);
      const surplusFrac = velDeals > 0 ? Math.max(0, (velDeals - overlap) / velDeals) : 0;
      adjustedVelocityGrid[m][div] = velocityGrid[m][div] * surplusFrac;
    }
  }

  // ---- COMBINE LAYERS: pipeline + adjusted velocity ----
  const revenueGrid = new Array(12).fill(null).map(() => ({ bwm: 0, bts: 0, bia: 0 }));
  for (let m = 0; m < 12; m++) {
    for (const div of DIVISIONS) {
      revenueGrid[m][div] = pipelineLayer.grid[m][div] + adjustedVelocityGrid[m][div];
    }
  }

  // Per-month, per-division breakdown for table/chart
  const forecast = [];
  for (let m = ACTUALS_CUTOFF_2026; m < 12; m++) {
    const row = { monthIdx: m, label: MONTH_LABELS[m] };
    for (const div of DIVISIONS) {
      const baseRate = velocity.rates[div];
      const seasonal = SEASONAL_MULTIPLIERS[div][m];
      const userAdj = adjustments[div] ?? 1.0;
      const prospectCount = baseRate * seasonal * userAdj * config.velocityMultiplier;
      const expectedDeals = prospectCount * conversionRates[div];
      row[`${div}Prospects`] = prospectCount;
      row[`${div}Deals`] = expectedDeals;
      row[`${div}Season`] = getSeasonLabel(div, m);
    }

    // Total expected closes for the month (sum across divisions)
    row.totalDeals = DIVISIONS.reduce((s, d) => s + (row[`${d}Deals`] || 0), 0);
    forecast.push(row);
  }

  // Compute monthly new revenue and cumulative + per-layer split
  let cumulative = 0;
  let pipelineTotal = 0;
  let velocityTotal = 0;
  for (const row of forecast) {
    const m = row.monthIdx;
    row.bwmRevenue = Math.round(revenueGrid[m].bwm);
    row.btsRevenue = Math.round(revenueGrid[m].bts);
    row.biaRevenue = Math.round(revenueGrid[m].bia);
    row.newRevenue = row.bwmRevenue + row.btsRevenue + row.biaRevenue;
    // Layer split for the row
    row.pipelineRevenue = Math.round(
      pipelineLayer.grid[m].bwm + pipelineLayer.grid[m].bts + pipelineLayer.grid[m].bia
    );
    row.velocityRevenue = Math.round(
      adjustedVelocityGrid[m].bwm + adjustedVelocityGrid[m].bts + adjustedVelocityGrid[m].bia
    );
    pipelineTotal += row.pipelineRevenue;
    velocityTotal += row.velocityRevenue;
    cumulative += row.newRevenue;
    row.cumulative = cumulative;
  }

  // Summary KPIs
  const totalNewRevenue = forecast.reduce((s, r) => s + r.newRevenue, 0);
  const totalNewClients = forecast.reduce((s, r) => s + r.totalDeals, 0);
  const forecastPlusCommitted = totalNewRevenue + (committedRevenue || 0);

  return {
    forecast,
    totalNewRevenue,
    totalNewClients,
    forecastPlusCommitted,
    pipelineTotal,
    velocityTotal,
    velocity,
    avgDealValues,
    conversionRates,
  };
}

// ---------------------------------------------------------------------------
// Per-division summary for What-If controls
// ---------------------------------------------------------------------------
function computeDivisionSummary(div, velocity, adjustments, conversionRates, avgDealValues) {
  const baseRate = velocity.rates[div];
  const userAdj = adjustments[div] ?? 1.0;
  const adjRate = baseRate * userAdj;

  // Sum expected deals across remaining months (with seasonal)
  let totalDeals = 0;
  for (let m = ACTUALS_CUTOFF_2026; m < 12; m++) {
    const seasonal = SEASONAL_MULTIPLIERS[div][m];
    totalDeals += adjRate * seasonal * conversionRates[div];
  }

  const totalRevenue = totalDeals * avgDealValues[div];
  return { baseRate, adjRate, totalDeals, totalRevenue };
}

// ---------------------------------------------------------------------------
// Custom tooltip for the chart
// ---------------------------------------------------------------------------
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-3 text-[12px]">
      <p className="font-bold text-navy mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }} className="mb-0.5">
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SmartForecast Component
// ---------------------------------------------------------------------------
export default function SmartForecast() {
  const { prospects, pipelineKPIs, sourceStats } = usePipeline();
  const { global2026Monthly, kpi, forecastScenario, setForecastScenario } = useFinancialData();

  // What-If adjustments: prospect multiplier + conversion rate override per division
  const [adjustments, setAdjustments] = useState({ bwm: 1.0, bts: 1.0, bia: 1.0 });
  const [convOverrides, setConvOverrides] = useState({ bwm: null, bts: null, bia: null }); // null = use baseline
  const hasAdjustments = adjustments.bwm !== 1.0 || adjustments.bts !== 1.0 || adjustments.bia !== 1.0
    || convOverrides.bwm !== null || convOverrides.bts !== null || convOverrides.bia !== null;

  const handleAdjustment = useCallback((div, value) => {
    setAdjustments((prev) => ({ ...prev, [div]: value }));
  }, []);

  const handleConvOverride = useCallback((div, value) => {
    setConvOverrides((prev) => ({ ...prev, [div]: value }));
  }, []);

  const resetAdjustments = useCallback(() => {
    setAdjustments({ bwm: 1.0, bts: 1.0, bia: 1.0 });
    setConvOverrides({ bwm: null, bts: null, bia: null });
  }, []);

  // Committed P&L revenue for 2026 = base P&L months + won-deals overlay.
  // The won-deals layer (closed-won prospects) is folded into the Dashboard
  // and P&L View totals; include it here too so the forecast baseline starts
  // from the same committed number the rest of the app shows.
  const committedRevenue = useMemo(() => {
    if (!global2026Monthly?.months) return 0;
    let total = 0;
    for (const mk of MONTH_KEYS) {
      const inc = global2026Monthly.months[mk]?.income;
      if (inc) total += Object.values(inc).reduce((s, v) => s + (v || 0), 0);
    }
    return total;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [global2026Monthly]);

  // Baseline forecast: scenario applied, no user adjustments
  const baseline = useMemo(
    () => runForecast(prospects, { bwm: 1.0, bts: 1.0, bia: 1.0 }, committedRevenue, null, forecastScenario, sourceStats),
    [prospects, committedRevenue, forecastScenario, sourceStats]
  );

  // What-if forecast: scenario + user adjustments
  const whatIf = useMemo(
    () => runForecast(prospects, adjustments, committedRevenue, convOverrides, forecastScenario, sourceStats),
    [prospects, adjustments, committedRevenue, convOverrides, forecastScenario, sourceStats]
  );

  // Use what-if for chart/table when adjustments are active, otherwise baseline
  const activeResult = hasAdjustments ? whatIf : baseline;
  const { forecast, totalNewRevenue, totalNewClients, forecastPlusCommitted, velocity, avgDealValues, conversionRates } = activeResult;

  // Chart data
  const chartData = useMemo(() => {
    return forecast.map((row) => ({
      month: row.label,
      BWM: row.bwmRevenue,
      BTS: row.btsRevenue,
      BIA: row.biaRevenue,
      Cumulative: row.cumulative,
    }));
  }, [forecast]);

  // Delta from baseline (for what-if comparison)
  const revenueDelta = whatIf.totalNewRevenue - baseline.totalNewRevenue;
  const clientDelta = whatIf.totalNewClients - baseline.totalNewClients;

  // Baseline chart data (always shows baseline)
  const baselineChartData = useMemo(() => {
    return baseline.forecast.map((row) => ({
      month: row.label,
      BWM: row.bwmRevenue, BTS: row.btsRevenue, BIA: row.biaRevenue,
      Cumulative: row.cumulative,
    }));
  }, [baseline]);

  // Per-division baseline breakdown for the narrative
  const divisionBreakdown = useMemo(() => {
    return DIVISIONS.map(div => {
      const totalProspects = baseline.forecast.reduce((s, r) => s + (r[`${div}Prospects`] || 0), 0);
      const totalDeals = baseline.forecast.reduce((s, r) => s + (r[`${div}Deals`] || 0), 0);
      const totalRevenue = baseline.forecast.reduce((s, r) => s + (r[`${div}Revenue`] || 0), 0);
      const rate = baseline.velocity.rates[div];
      const conv = conversionRates[div];
      const dealVal = avgDealValues[div];
      const hasObservedRate = baseline.velocity.hasData;
      // Days to close from won deals
      const daysData = computeAvgDaysToClose(prospects);
      const daysToClose = daysData[div];
      const daysObserved = daysData.hasData[div];
      return { div, totalProspects, totalDeals, totalRevenue, rate, conv, dealVal, hasObservedRate, daysToClose, daysObserved };
    });
  }, [baseline, conversionRates, avgDealValues, prospects]);

  return (
    <div className="space-y-8">

      {/* ================================================================= */}
      {/*  SMART FORECAST - the auto-correcting prediction                  */}
      {/* ================================================================= */}
      <div className="border-l-4 border-deep-teal pl-0">

        {/* Header with scenario toggle */}
        <div className="bg-deep-teal/5 rounded-r-[4px] px-5 py-4 mb-4 flex items-start justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-[16px] font-bold text-navy">Smart Forecast</h2>
            <p className="text-[12px] text-[#888] mt-1">
              3-layer projection (Committed + Pipeline + Velocity) for the rest of 2026.
              Pipeline = existing prospects with smart probability. Velocity = new prospects, time-shifted by sales cycle.
            </p>
          </div>
          {/* Scenario toggle - persists globally */}
          <div className="flex flex-col items-end gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#888]">Scenario</span>
            <div className="flex items-center bg-white border border-gray-200 rounded-[4px] p-0.5 shadow-sm">
              {['conservative', 'normal', 'aggressive'].map(s => {
                const cfg = SCENARIO_CONFIG[s];
                const active = forecastScenario === s;
                const activeBg = s === 'conservative' ? 'bg-rose text-white' : s === 'aggressive' ? 'bg-deep-teal text-white' : 'bg-navy text-white';
                return (
                  <button
                    key={s}
                    onClick={() => setForecastScenario(s)}
                    className={`px-3 py-1 text-[11px] font-bold rounded-[3px] transition-colors ${
                      active ? activeBg : 'text-[#888] hover:text-navy'
                    }`}
                    title={`${cfg.label}: pipeline ${cfg.pipelineMultiplier}x, velocity ${cfg.velocityMultiplier}x`}
                  >
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Layer breakdown summary */}
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4 mb-4 mx-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-deep-teal mb-3">Forecast Composition (Apr-Dec)</p>
          <div className="grid grid-cols-3 gap-4 text-[12px]">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#888] mb-1">Pipeline (current prospects)</div>
              <div className="text-[18px] font-bold text-navy">{formatCurrency(baseline.pipelineTotal)}</div>
              <div className="text-[10px] text-[#999] mt-0.5">Existing deals × probability, recognized at expected close</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#888] mb-1">Velocity (new prospects)</div>
              <div className="text-[18px] font-bold text-navy">{formatCurrency(baseline.velocityTotal)}</div>
              <div className="text-[10px] text-[#999] mt-0.5">Projected new deals, time-shifted by sales cycle</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#888] mb-1">Total New Revenue</div>
              <div className="text-[18px] font-bold text-deep-teal">{formatCurrency(baseline.totalNewRevenue)}</div>
              <div className="text-[10px] text-[#999] mt-0.5">Pipeline + velocity (overlaps subtracted)</div>
            </div>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-3 gap-4 mb-5 px-1">
          <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-deep-teal mb-1">Committed Revenue</p>
            <p className="text-[28px] font-bold text-navy leading-tight">{formatCurrency(committedRevenue)}</p>
            <p className="text-[10px] font-light text-[#888] mt-1">Contracted from P&L (subscriptions, tax prep, investment fees)</p>
          </div>
          <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-deep-teal mb-1">Forecast New Revenue</p>
            <p className="text-[28px] font-bold text-navy leading-tight">{formatCurrency(baseline.totalNewRevenue)}</p>
            <p className="text-[10px] font-light text-[#888] mt-1">{SCENARIO_CONFIG[forecastScenario].label} scenario · ~{Math.round(baseline.totalNewClients)} new clients</p>
          </div>
          <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 border-2 border-deep-teal/20">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-deep-teal mb-1">Total 2026 Forecast</p>
            <p className="text-[28px] font-bold text-deep-teal leading-tight">{formatCurrency(baseline.forecastPlusCommitted)}</p>
            <p className="text-[10px] font-light text-[#888] mt-1">Committed + forecast new client revenue</p>
          </div>
        </div>

        {/* Forecast Insight - the narrative explaining what's happening */}
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 mb-5 mx-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-deep-teal mb-3">Forecast Breakdown by Division</p>
          <div className="space-y-4">
            {divisionBreakdown.map(d => {
              const monthsRemaining = 12 - ACTUALS_CUTOFF_2026;
              return (
                <div key={d.div} className="flex gap-4 items-start border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                  <div className="w-16 shrink-0">
                    <span className="text-[14px] font-bold" style={{ color: DIVISION_COLORS[d.div] }}>{DIVISION_LABELS[d.div]}</span>
                  </div>
                  <div className="flex-1 text-[12px] text-near-black leading-relaxed">
                    <p>
                      At the current pace of <span className="font-bold">{d.rate.toFixed(1)} prospects/month</span>
                      {d.hasObservedRate ? ' (observed)' : ' (estimated)'},
                      the forecast projects <span className="font-bold">{d.totalProspects.toFixed(0)} total prospects</span> entering
                      the pipeline over the next {monthsRemaining} months.
                    </p>
                    <p className="mt-1">
                      With a <span className="font-bold">{(d.conv * 100).toFixed(0)}% conversion rate</span>,
                      avg deal value of <span className="font-bold">{formatCurrency(d.dealVal)}</span>,
                      and <span className="font-bold">{d.daysToClose} days to close</span>
                      {d.daysObserved ? ' (observed)' : ' (default)'},
                      that produces <span className="font-bold text-deep-teal">{Math.round(d.totalDeals)} new clients</span> generating
                      {' '}<span className="font-bold text-deep-teal">{formatCurrency(Math.round(d.totalRevenue))}</span> in new revenue.
                    </p>
                    {d.div === 'bts' && (
                      <p className="mt-1 text-[11px] text-[#999]">
                        Tax season boost factored in: higher prospect flow Jan-Apr and Sep-Oct, slower May-Aug and Nov-Dec.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Baseline chart */}
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 mb-5 mx-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-deep-teal mb-3">Baseline Monthly Projection</p>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={baselineChartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#888' }} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#888' }} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#888' }} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="BWM" name="BWM" fill={DIVISION_COLORS.bwm} stackId="revenue" />
              <Bar yAxisId="left" dataKey="BTS" name="BTS" fill={DIVISION_COLORS.bts} stackId="revenue" />
              <Bar yAxisId="left" dataKey="BIA" name="BIA" fill={DIVISION_COLORS.bia} stackId="revenue" radius={[2, 2, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="Cumulative" name="Cumulative" stroke="#022889" strokeWidth={2} dot={{ r: 3, fill: '#022889' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Baseline detail table */}
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 mx-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-deep-teal mb-3">Monthly Detail</p>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-deep-teal text-white">
                  <th className="px-3 py-2 text-left font-bold">Month</th>
                  <th className="px-3 py-2 text-center font-bold">BWM Prospects</th>
                  <th className="px-3 py-2 text-center font-bold">BTS Prospects</th>
                  <th className="px-3 py-2 text-center font-bold">BIA Prospects</th>
                  <th className="px-3 py-2 text-right font-bold">Expected Closes</th>
                  <th className="px-3 py-2 text-right font-bold">New Revenue</th>
                  <th className="px-3 py-2 text-right font-bold">Cumulative</th>
                </tr>
              </thead>
              <tbody>
                {baseline.forecast.map((row, i) => (
                  <tr key={row.monthIdx} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-3 py-2 font-bold text-navy">{row.label}</td>
                    {DIVISIONS.map((div) => (
                      <td key={div} className="px-3 py-2 text-center">
                        <span className="text-navy">{(row[`${div}Prospects`] || 0).toFixed(1)}</span>
                        <SeasonBadge label={row[`${div}Season`]} />
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right text-navy">{(row.totalDeals || 0).toFixed(1)}</td>
                    <td className="px-3 py-2 text-right text-navy">{formatCurrency(row.newRevenue)}</td>
                    <td className="px-3 py-2 text-right font-bold text-deep-teal">{formatCurrency(row.cumulative)}</td>
                  </tr>
                ))}
                <tr className="bg-deep-teal/10 border-t-2 border-deep-teal/30">
                  <td className="px-3 py-2 font-bold text-navy">Total</td>
                  {DIVISIONS.map((div) => {
                    const total = baseline.forecast.reduce((s, r) => s + (r[`${div}Prospects`] || 0), 0);
                    return <td key={div} className="px-3 py-2 text-center font-bold text-navy">{total.toFixed(0)}</td>;
                  })}
                  <td className="px-3 py-2 text-right font-bold text-navy">{Math.round(baseline.totalNewClients)}</td>
                  <td className="px-3 py-2 text-right font-bold text-navy">{formatCurrency(baseline.totalNewRevenue)}</td>
                  <td className="px-3 py-2 text-right font-bold text-deep-teal">{formatCurrency(baseline.forecast[baseline.forecast.length - 1]?.cumulative || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/*  WHAT-IF SCENARIO - the exploration tool                          */}
      {/* ================================================================= */}
      <div className="border-l-4 border-bullogic-blue pl-0">

        <div className="bg-bullogic-blue/5 rounded-r-[4px] px-5 py-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[16px] font-bold text-navy">What-If Scenario</h2>
              <p className="text-[12px] text-[#888] mt-1">
                Don't like the baseline numbers? Adjust prospect flow per division to see what it takes to hit your goals.
              </p>
            </div>
            {hasAdjustments && (
              <button onClick={resetAdjustments}
                className="px-4 py-1.5 text-[11px] font-bold text-[#888] bg-white border border-gray-200 rounded-[4px] hover:bg-gray-50">
                Reset to Baseline
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 mx-1">
          {/* Column headers */}
          <div className="flex items-center gap-3 pb-2 mb-1 border-b border-gray-200">
            <div className="w-28 shrink-0 text-[10px] font-bold uppercase tracking-[0.08em] text-[#999]">Division</div>
            <div className="flex-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#999] text-center">Prospect Flow</div>
            <div className="w-40 shrink-0 text-[10px] font-bold uppercase tracking-[0.08em] text-[#999] text-center">Conversion Rate</div>
            <div className="w-64 shrink-0 text-[10px] font-bold uppercase tracking-[0.08em] text-[#999] text-right">Result</div>
          </div>

          <div className="space-y-2">
            {DIVISIONS.map((div) => {
              const baseConv = baseline.conversionRates[div];
              const activeConv = convOverrides[div] != null ? convOverrides[div] : baseConv;
              const whatIfConvRates = { ...baseline.conversionRates };
              if (convOverrides[div] != null) whatIfConvRates[div] = convOverrides[div];

              const baselineSummary = computeDivisionSummary(div, baseline.velocity, { bwm: 1, bts: 1, bia: 1 }, baseline.conversionRates, avgDealValues);
              const whatIfSummary = computeDivisionSummary(div, baseline.velocity, adjustments, whatIfConvRates, avgDealValues);
              const sliderValue = adjustments[div];
              const adjRate = (baseline.velocity.rates[div] * sliderValue).toFixed(1);

              return (
                <div key={div} className="flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-b-0">
                  {/* Division label */}
                  <div className="w-28 shrink-0">
                    <span className="text-[13px] font-bold" style={{ color: DIVISION_COLORS[div] }}>{DIVISION_LABELS[div]}</span>
                    <p className="text-[10px] text-[#888]">{baselineSummary.baseRate.toFixed(1)}/mo base</p>
                  </div>

                  {/* Prospect flow slider */}
                  <div className="flex-1 flex items-center gap-2">
                    <span className="text-[11px] font-bold text-navy w-10 text-right">{adjRate}/mo</span>
                    <input type="range" min="0" max="3" step="0.1" value={sliderValue}
                      onChange={(e) => handleAdjustment(div, parseFloat(e.target.value))}
                      className="flex-1 h-2 cursor-pointer rounded-lg"
                      style={{ accentColor: DIVISION_COLORS[div] }} />
                    <span className="text-[10px] text-[#aaa] w-6">{sliderValue.toFixed(1)}x</span>
                  </div>

                  {/* Conversion rate slider */}
                  <div className="w-40 shrink-0 flex items-center gap-2">
                    <span className="text-[11px] font-bold text-navy w-10 text-right">{(activeConv * 100).toFixed(0)}%</span>
                    <input type="range" min="0" max="100" step="5" value={Math.round(activeConv * 100)}
                      onChange={(e) => {
                        const pct = parseInt(e.target.value) / 100;
                        handleConvOverride(div, Math.abs(pct - baseConv) < 0.01 ? null : pct);
                      }}
                      className="flex-1 h-2 cursor-pointer rounded-lg"
                      style={{ accentColor: DIVISION_COLORS[div] }} />
                  </div>

                  {/* Result */}
                  <div className="w-64 shrink-0 text-[12px] text-right">
                    <span className="font-bold text-navy">{Math.round(whatIfSummary.totalDeals)}</span>
                    <span className="text-[#aaa] mx-1">clients</span>
                    <span className="font-bold text-bullogic-blue">{formatCurrency(Math.round(whatIfSummary.totalRevenue))}</span>
                    {(sliderValue !== 1.0 || convOverrides[div] != null) && (
                      <span className={`ml-2 text-[11px] font-bold ${whatIfSummary.totalRevenue > baselineSummary.totalRevenue ? 'text-deep-teal' : 'text-rose'}`}>
                        ({whatIfSummary.totalRevenue >= baselineSummary.totalRevenue ? '+' : ''}{formatCurrency(Math.round(whatIfSummary.totalRevenue - baselineSummary.totalRevenue))})
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* What-if impact summary */}
          <div className={`mt-4 pt-3 border-t-2 flex items-center justify-between ${hasAdjustments ? 'border-bullogic-blue/30' : 'border-gray-200'}`}>
            <span className="text-[12px] text-[#888]">{hasAdjustments ? 'Impact vs baseline:' : 'Sliders at baseline (1.0x). Adjust to explore.'}</span>
            {hasAdjustments && (
              <div className="flex gap-4 text-[13px]">
                <span>
                  <span className={`font-bold ${revenueDelta >= 0 ? 'text-deep-teal' : 'text-rose'}`}>
                    {revenueDelta >= 0 ? '+' : ''}{formatCurrency(revenueDelta)}
                  </span>
                  <span className="text-[#888] ml-1">revenue</span>
                </span>
                <span>
                  <span className={`font-bold ${clientDelta >= 0 ? 'text-deep-teal' : 'text-rose'}`}>
                    {clientDelta >= 0 ? '+' : ''}{Math.round(clientDelta)}
                  </span>
                  <span className="text-[#888] ml-1">clients</span>
                </span>
                <span>
                  <span className="font-bold text-navy">{formatCurrency(whatIf.forecastPlusCommitted)}</span>
                  <span className="text-[#888] ml-1">total 2026</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Season badge component
// ---------------------------------------------------------------------------
function SeasonBadge({ label }) {
  if (!label || label === 'Normal') return null;

  const isPeak = label === 'Peak';
  const bgColor = isPeak ? 'bg-deep-teal/10' : 'bg-rose/10';
  const textColor = isPeak ? 'text-deep-teal' : 'text-rose';

  return (
    <span className={`ml-1 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${bgColor} ${textColor}`}>
      {label}
    </span>
  );
}
